/*
 * Product hero fit
 *
 * On desktop, the first product image sits in a landscape box (see sypnosis.css). Photos are
 * framed differently, so a single scale suits some products and leaves others tiny. This finds
 * the product's outline in the photo (everything that differs from the background colour) and
 * zooms until the product reaches a target share of the box width or height, whichever comes
 * first. It never goes below the CSS default scale, never past filling the box width, and never
 * crops the product.
 *
 * Add "zoom-110" (any percentage) to the first image's alt text to adjust its size from there;
 * it's still kept from cropping the product.
 *
 * Rotated heroes (--hero-rotate set, e.g. the incense packs) keep their fixed CSS scale.
 * If the image can't be analysed, the CSS default (--hero-scale fallback) is used.
 *
 * Gallery images with the alt text "full" are cropped to fill their frame (sypnosis.css). When
 * such a photo has plain background down both its left and right edges (a tall product that
 * runs top to bottom), cropping would cut the product, so it always spans the frame height
 * instead (trimming only plain background at the sides if the frame is narrower than the photo),
 * with the frame filled in the photo's own background colour. Add "crop" to the alt text
 * ("full crop") to always keep an image cropped.
 */
(function () {
  const SELECTOR =
    '.product__media-wrapper .product__media-list li:first-of-type .product__media.media img';
  const FULL_SELECTOR = '.product__media-wrapper .product__media-list li:not(:first-of-type) .product__media.full img';
  const PLAIN_EDGE = 0.98; // share of an edge column that must match the background
  const DESKTOP = window.matchMedia('(min-width: 750px)');
  const TARGET_WIDTH = 0.65; // product may grow to this share of the box width...
  const TARGET_HEIGHT = 0.8; // ...or this share of the box height, whichever is reached first
  const MIN_SCALE = 1.09; // matches the CSS default, so nothing gets smaller than before
  const EDGE_MARGIN = 0.02; // fraction of the box kept clear around the product
  const SAMPLE_WIDTH = 160; // analysis resolution
  const THRESHOLD = 40; // summed RGB difference from the background that counts as product

  const boundsCache = new WeakMap();

  // Draws the image small and returns its pixels plus the background colour (from the corners)
  function sample(img) {
    const width = SAMPLE_WIDTH;
    const height = Math.round((width * img.naturalHeight) / img.naturalWidth);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(img, 0, 0, width, height);
    let data;
    try {
      data = context.getImageData(0, 0, width, height).data;
    } catch (error) {
      return null; // cross-origin image
    }
    const corners = [0, (width - 1) * 4, (height - 1) * width * 4, ((height - 1) * width + width - 1) * 4];
    const background = [0, 1, 2].map((channel) => corners.reduce((sum, i) => sum + data[i + channel], 0) / 4);
    return { data, width, height, background };
  }

  function isProductPixel(pixels, x, y) {
    const i = (y * pixels.width + x) * 4;
    const { data, background } = pixels;
    return (
      Math.abs(data[i] - background[0]) + Math.abs(data[i + 1] - background[1]) + Math.abs(data[i + 2] - background[2]) >
      THRESHOLD
    );
  }

  function fitFullImage(img) {
    const pixels = sample(img);
    if (!pixels) return;
    const plainShare = (x) => {
      let plain = 0;
      for (let y = 0; y < pixels.height; y++) if (!isProductPixel(pixels, x, y)) plain++;
      return plain / pixels.height;
    };
    const frame = img.closest('.product__media');
    const forceCrop = frame.classList.contains('crop');
    const sidesPlain = plainShare(1) >= PLAIN_EDGE && plainShare(pixels.width - 2) >= PLAIN_EDGE;

    if (sidesPlain && !forceCrop) {
      // Span the frame height. If the frame is narrower than the photo, that trims the plain
      // sides (object-fit: cover) -- but only while the product itself still fits across.
      const box = frame.getBoundingClientRect();
      const photoAspect = img.naturalWidth / img.naturalHeight;
      let fitMode = 'contain';
      if (box.width / box.height < photoAspect) {
        let minX = pixels.width;
        let maxX = -1;
        for (let y = 0; y < pixels.height; y++) {
          for (let x = 0; x < pixels.width; x++) {
            if (isProductPixel(pixels, x, y)) {
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
            }
          }
        }
        const visibleShare = box.width / (box.height * photoAspect); // share of photo width left after trimming
        const productReach = Math.max(0.5 - minX / pixels.width, (maxX + 1) / pixels.width - 0.5);
        if (productReach <= visibleShare / 2) fitMode = 'cover';
      }
      img.style.objectFit = fitMode;
      frame.style.backgroundColor = `rgb(${pixels.background.map(Math.round).join(', ')})`;
    } else {
      img.style.objectFit = '';
      frame.style.backgroundColor = '';
    }
  }

  function productBounds(img) {
    const cached = boundsCache.get(img);
    if (cached && cached.src === img.currentSrc) return cached.bounds;

    const pixels = sample(img);
    if (!pixels) return null; // keep the CSS default
    const { width, height } = pixels;

    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (isProductPixel(pixels, x, y)) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return null;

    const bounds = { x0: minX / width, x1: (maxX + 1) / width, y0: minY / height, y1: (maxY + 1) / height };
    boundsCache.set(img, { src: img.currentSrc, bounds });
    return bounds;
  }

  function fit(img) {
    if (!DESKTOP.matches) {
      img.style.removeProperty('--hero-scale');
      return;
    }
    const rotation = getComputedStyle(img).getPropertyValue('--hero-rotate').trim();
    if (rotation && rotation !== '0deg') return;

    const box = img.parentElement.getBoundingClientRect();
    if (box.width < 10 || box.height < 10 || !img.naturalWidth) return;

    const bounds = productBounds(img);
    if (!bounds) return;

    // Size of the photo as drawn with object-fit: contain, before scaling
    const aspect = img.naturalWidth / img.naturalHeight;
    let photoWidth;
    let photoHeight;
    if (box.width / box.height > aspect) {
      photoHeight = box.height;
      photoWidth = box.height * aspect;
    } else {
      photoWidth = box.width;
      photoHeight = box.width / aspect;
    }

    // Furthest the product reaches from the photo's centre, as a fraction of the photo
    const reachX = Math.max(0.5 - bounds.x0, bounds.x1 - 0.5);
    const reachY = Math.max(0.5 - bounds.y0, bounds.y1 - 0.5);

    // Largest scale that keeps the product inside the box (it may be off-centre in the photo)
    const fitX = (box.width * (0.5 - EDGE_MARGIN)) / (reachX * photoWidth);
    const fitY = (box.height * (0.5 - EDGE_MARGIN)) / (reachY * photoHeight);
    const fillWidth = box.width / photoWidth;

    // Scale at which the product reaches the target size
    const targetX = (box.width * TARGET_WIDTH) / ((bounds.x1 - bounds.x0) * photoWidth);
    const targetY = (box.height * TARGET_HEIGHT) / ((bounds.y1 - bounds.y0) * photoHeight);

    // Optional per-image adjustment from the alt text, e.g. "zoom-110" = 10% larger
    const zoomClass = [...img.closest('.product__media').classList].find((name) => /^zoom-\d+$/.test(name));
    const zoom = zoomClass ? parseInt(zoomClass.slice(5), 10) / 100 : 1;

    const target = Math.max(MIN_SCALE, Math.min(targetX, targetY)) * zoom;
    const scale = Math.max(1, Math.min(target, fillWidth, fitX, fitY));
    img.style.setProperty('--hero-scale', scale.toFixed(3));
  }

  function whenLoaded(img, callback) {
    if (img.complete && img.naturalWidth) {
      callback(img);
    } else {
      img.addEventListener('load', () => callback(img), { once: true });
    }
  }

  function run() {
    document.querySelectorAll(SELECTOR).forEach((img) => whenLoaded(img, fit));
  }

  function runFullImages() {
    document.querySelectorAll(FULL_SELECTOR).forEach((img) => whenLoaded(img, fitFullImage));
  }

  // Recalculate whenever an image frame changes size (window resize, layout changes)
  let resizeTimer;
  function scheduleRefit() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      run();
      runFullImages();
    }, 150);
  }
  window.addEventListener('resize', scheduleRefit);
  if ('ResizeObserver' in window) {
    const frameSizes = new WeakMap();
    const observer = new ResizeObserver((entries) => {
      const changed = entries.some(({ target, contentRect }) => {
        const previous = frameSizes.get(target);
        frameSizes.set(target, `${Math.round(contentRect.width)}x${Math.round(contentRect.height)}`);
        return previous && previous !== frameSizes.get(target);
      });
      if (changed) scheduleRefit();
    });
    const observeFrames = () =>
      document.querySelectorAll(`${SELECTOR}, ${FULL_SELECTOR}`).forEach((img) => observer.observe(img.closest('.product__media')));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observeFrames);
    else observeFrames();
  }
  DESKTOP.addEventListener('change', run);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      run();
      runFullImages();
    });
  } else {
    run();
    runFullImages();
  }
})();
